import type { Swagpack } from "../../../../../../models/Swagstore/Packs";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";

export type CatalogSkuChoice = FilterValue;

export const PRODUCT_LEVEL_SKU = "__all__";

export function catalogSkuForRequest(selectedSku: string): string | null {
  const sku = selectedSku.trim();
  if (!sku || sku === PRODUCT_LEVEL_SKU) return null;
  return sku;
}

export function catalogSkuOf(variant: {
  variant_sku?: string | null;
  client_sku?: string | null;
}): string {
  return String(variant.variant_sku || variant.client_sku || "").trim();
}

/** A missing active flag stays listed. `false` and `0` are inactive catalog SKUs. */
export function isActiveCatalogSku(variant: {
  active?: boolean | number | null;
}): boolean {
  if (variant.active === undefined || variant.active === null) return true;
  return variant.active !== false && Number(variant.active) !== 0;
}

/** Unique active catalog SKUs, in the order they were given. */
export function activeCatalogSkus(
  rows: Array<{ sku?: string | null; active?: boolean | number | null }>,
): string[] {
  const seen = new Set<string>();
  const skus: string[] = [];
  for (const row of rows) {
    if (!isActiveCatalogSku(row)) continue;
    const sku = String(row.sku ?? "").trim();
    const key = sku.toUpperCase();
    if (!sku || seen.has(key)) continue;
    seen.add(key);
    skus.push(sku);
  }
  return skus;
}

export function catalogSkuChoices(product: Swagpack | null): CatalogSkuChoice[] {
  const choices: CatalogSkuChoice[] = [
    { id: PRODUCT_LEVEL_SKU, value: "All SKUs" },
  ];
  const seen = new Set<string>();
  for (const packProduct of product?.products ?? []) {
    for (const variant of packProduct.variants ?? []) {
      if (!isActiveCatalogSku(variant)) continue;
      const sku = catalogSkuOf(variant);
      if (!sku) continue;
      const key = sku.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      choices.push({ id: sku, value: sku });
    }
  }
  return choices;
}

/**
 * A product PSD belongs only to All SKUs. A SKU shows its own PSD and nothing else.
 */
export function psdMatchesCatalogSku(
  variantSku: string | null | undefined,
  selectedSku: string,
): boolean {
  const row = String(variantSku ?? "").trim().toUpperCase();
  const wanted = (catalogSkuForRequest(selectedSku) ?? "").toUpperCase();
  if (!wanted) return row.length === 0;
  return row === wanted;
}

export function mockupMatchesCatalogSku(
  variantSku: string | null | undefined,
  selectedSku: string,
): boolean {
  const row = String(variantSku ?? "").trim().toUpperCase();
  const wanted = (catalogSkuForRequest(selectedSku) ?? "").toUpperCase();
  if (!wanted) return true;
  return row === wanted;
}
